import React, { useState } from "react";
import type { Worker, Location } from "../types";
import { useLang } from "../LangContext";

export const WEBSITE_URL = window.location.origin + '/' || 'http://localhost:3000/';

interface Props {
  worker: Worker | null;
  locations: Location[];
  shareDuration: string;
  shareLocationId: string;
  onShareDurationChange: (value: string) => void;
  onShareLocationIdChange: (value: string) => void;
}

export default function SchedulingLinkCard({
  worker,
  locations,
  shareDuration,
  shareLocationId,
  onShareDurationChange,
  onShareLocationIdChange,
}: Props) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const buildShareUrl = (): string => {
    const id = worker?.custom_public_id || worker?.public_id;
    const base = `${WEBSITE_URL}book/${id}`;
    const params: string[] = [];
    const isDefaultDuration = !shareDuration || shareDuration === String(worker?.default_duration);
    const isDefaultLocation = !shareLocationId || locations.find((l) => String(l.id) === shareLocationId)?.is_default;
    if (!isDefaultDuration) params.push(`duration=${shareDuration}`);
    if (!isDefaultLocation) params.push(`location=${shareLocationId}`);
    return params.length ? `${base}?${params.join("&")}` : base;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildShareUrl()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="card scheduling-link-card">
      <h3 className="card-title">{t.schedulingLinkHeading}</h3>

      <div className="share-field">
        <label htmlFor="shareDuration">{t.chooseDurationLabel}</label>
        <select
          id="shareDuration"
          className="share-select"
          value={shareDuration}
          onChange={(e) => onShareDurationChange(e.target.value)}
        >
          <option value="">{t.customerChooses}</option>
          {(worker?.duration_options || []).map((d) => (
            <option key={d} value={d}>
              {d === 1 ? t.oneHour : t.nHours(d)}
            </option>
          ))}
        </select>
      </div>

      <div className="share-field">
        <label htmlFor="shareLocation">{t.chooseAddressLabel}</label>
        {locations.length === 0 ? (
          <p className="share-empty-hint">
            {t.noLocationsHint}
          </p>
        ) : (
          <select
            id="shareLocation"
            className="share-select"
            value={shareLocationId}
            onChange={(e) => onShareLocationIdChange(e.target.value)}
          >
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}{loc.is_default ? ` (${t.defaultBadge.toLowerCase()})` : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="share-link-container">
        <code className="share-link">{buildShareUrl()}</code>
        <button
          className="copy-button"
          onClick={handleCopy}
        >
          {copied ? t.copiedButton : t.copyButton}
        </button>
      </div>
    </div>
  );
}
